<template>
  <div class="voting-ideas-content">
    <div class="left-column" @click="removePickOption()">
      <ul>
        <li :class="{'disabled': $store.state.sharePresentation}">
          <div
            @click="selectTab(tabName.SUBMISSION)"
            class="relative"
          >
            <span
              class="voting-ideas-step horizontal layout center"
              :class="{'active': slideActive.votingStep === tabName.SUBMISSION}"
            >
              <span
                class="voting-ideas-step-number"
                :class="{'dark': isTextColourLight}"
              >1</span>
              <span class="voting-ideas-step-text">{{ $t('submission') }}</span>
            </span>
          </div>
          <div
            class="pl-10"
            @click.stop="toggleShowSubmission()"
            v-if="slideActive.votingStep === tabName.SUBMISSION"
          >
            <aha-icon
              :style="{ 'text-color': slideActive.textColour }"
              :name="slideActive.showSubmissions ? 'hide' : 'unhide'"
              class="mr-1"
            />
            <span class="text-base">
              <span v-if="slideActive.showSubmissions">{{ $t('hideSubmission') }}</span>
              <span v-else>{{ $t('showSubmission') }}</span>
            </span>
          </div>
        </li>
        <li
          :class="{'disabled': slideActive.SlideOptions.length === 0 ||
            $store.state.sharePresentation}"
        >
          <div
            @click="slideActive.SlideOptions.length === 0 ? false : selectTab(tabName.VOTING)"
          >
            <span
              class="voting-ideas-step horizontal layout center"
              :class="{'active': slideActive.votingStep === tabName.VOTING}"
            >
              <span
                class="voting-ideas-step-number"
                :class="{'dark': isTextColourLight}"
              >2</span>
              <span class="voting-ideas-step-text">{{ $t('voting') }}</span>
              <span
                class="pl-1 text-lg font-bold text-base-70"
              >
                ({{ (getSumVoteCount() || 0) | formatTotalPoint }})
              </span>
            </span>
          </div>
          <div
            class="pl-10"
            @click.stop="toggleShowVotes()"
            v-if="slideActive.votingStep === tabName.VOTING"
          >
            <aha-icon
              :style="{ 'text-color': slideActive.textColour }"
              :name="slideActive.showVotes ? 'hide' : 'unhide'"
              class="mr-1"
            />
            <span class="text-base">
              <span v-if="slideActive.showVotes">{{ $t('hideVotes') }}</span>
              <span v-else>{{ $t('showVotes') }}</span>
            </span>
          </div>
        </li>
        <li :class="{'disabled': isVotingStepSubmission}">
          <div
            @click="isVotingStepSubmission ? false : selectTab(tabName.RESULT)"
          >
            <span
              class="voting-ideas-step horizontal layout center"
              :class="{'active': slideActive.votingStep === tabName.RESULT}"
            >
              <span
                class="voting-ideas-step-number"
                :class="{'dark': isTextColourLight}"
              >3</span>
              <span class="voting-ideas-step-text">{{ $t('result') }}</span>
            </span>
          </div>
        </li>
      </ul>
    </div>
    <div
      class="right-column vertical layout"
    >
      <!-- ARROW UP -->
      <transition name="fade">
        <div
          v-if="arrowScrollTopVisible"
          class="horizontal layout center center-justified fix-height"
        >
          <div @click="scrollTop" class="arrow-grid no-absolute">
            <i class="fas fa-chevron-up" />
          </div>
        </div>
      </transition>
      <!-- End of ARROW UP -->
      <IdeasLayout
        :options="slideActive.SlideOptions"
        @pickOption="pickOption"
        :masonryTransitionDuration="masonryTransitionDuration"
        @removeResponse="handleRemoveResponse"
        v-if="loadedIdeas"
      />
      <div v-else class="horizontal layout center center-justified">
        <Loading />
      </div>
      <!-- IDEA PICKED -->
      <div
        class="pick-openended horizontal layout center center-justified"
        v-show="showIdea"
        @click="removePickOption()"
      >
        <transition name="popup">
          <div
            class="open-ended-picked"
            :class="{'has-audience-info': itemPicked.audienceName ||
                       itemPicked.audienceTeam || itemPicked.audienceEmoji ||
                       itemPicked.audienceEmail ||
                       slideActive.type === 'ideas',
                     'has-ideas-rank': isVotingStepResult
            }"
            v-if="itemPicked.id"
            :style="{
              'box-shadow': setBoxShadowValue(itemPicked, itemPicked.index),
              backgroundColor: slideActiveBaseColour }"
          >
            <p class="picked-title">
              <span
                class="content-rank-order"
                :style="{
                  'background-color': getRankOrderBg(),
                  'box-shadow': setBoxShadowValue(itemPicked, itemPicked.index),
                }"
              >
                <span
                  class="font-bold"
                  :class="{
                    'text-3xl': itemPicked.rankOrder <= 9,
                    'text-2xl': itemPicked.rankOrder > 9 && itemPicked.rankOrder <= 99,
                    'text-lg': itemPicked.rankOrder > 99
                  }"
                >
                  {{ itemPicked.rankOrder }}
                </span>
              </span>
              <ui-pre-line
                :text="itemPicked.title"
                class="has-transition"
                :class="{'hide-submission': isBlurAnswerContent}"
              />
            </p>
            <div
              class="audience-info"
              :class="{'dark': isTextColourLight,
                       'has-vote-count-content': slideActive.type === 'ideas'
                         && slideActive.votingStep !== tabName.SUBMISSION,
                       'has-min-height': slideActive.type === 'ideas'}"
              v-show="checkShowAudienceInfo(itemPicked)"
            >
              <div>
                <div class="truncate">
                  <span
                    class="info--emoji"
                    v-if="checkShowAudienceFields('avatar', itemPicked.audienceEmoji)"
                  >
                    {{ itemPicked.audienceEmoji }}
                  </span>
                  <span
                    class="font-weight-bold"
                    v-if="checkShowAudienceFields('name',itemPicked.audienceName)"
                  >
                    <profanity-filter :text="itemPicked.audienceName" />
                  </span>
                </div>
                <div>
                  <p
                    class="info--other-team truncate"
                    v-if="checkShowAudienceFields('team', itemPicked.audienceTeam)"
                  >
                    <profanity-filter :text="itemPicked.audienceTeam" />
                  </p>
                  <p
                    class="info--other-email truncate"
                    v-if="checkShowAudienceFields('email', itemPicked.audienceEmail)"
                  >
                    <profanity-filter :text="itemPicked.audienceEmail" />
                  </p>
                </div>
              </div>
              <div
                class="info--vote-count"
                v-show="slideActive.type === 'ideas'
                  && slideActive.votingStep !== tabName.SUBMISSION"
              >
                <aha-icon-container
                  size="lg"
                  icon-class="bg-base-0 text-base-90 vote-count-container"
                >
                  <aha-icon
                    name="like"
                    class="text-base-50 mb-0.5"
                    width="21"
                    height="21"
                    viewBox="0 0 24 24"
                  />
                  <span
                    class="pl-1 text-lg font-bold text-base-90"
                    v-show="isVoteCountTextVisible"
                  >{{ itemPicked.votesCount | formatTotalPoint }}</span>
                </aha-icon-container>
              </div>
            </div>
          </div>
        </transition>
      </div>
      <!-- End of IDEA PICKED -->

      <!-- ARROW DOWN -->
      <div class="relative horizontal layout center end-justified fix-height">
        <transition name="fade">
          <div
            class="arrow-grid"
            v-if="arrowScrollBottomVisible"
            :class="{ 'disabled': scrollDisable }"
          >
            <i class="fas fa-chevron-down" @click="scrollBottom" />
          </div>
        </transition>
      </div>
      <!-- End of ARROW DOWN -->
    </div>
  </div>
</template>

<script>
import IdeasLayout from '@/components-v2/presentation-editor/middle/IdeasLayout.vue'
import PreLine from '@/components/UI/PreLine.vue'
import ProfanityFilter from '@/components-v2/common/utils/ProfanityFilter.vue'
import Loading from '@/components/Loading'

export default {
  components: {
    IdeasLayout,
    ProfanityFilter,
    UiPreLine: PreLine,
    Loading,
  },
  props: {
    isLoaded: {
      type: Boolean,
      default: false,
    },
    slideActive: {
      type: Object,
      default: () => {},
    },
    masonryTransitionDuration: {
      type: Number,
      default: 0,
    },
    tabName: {
      type: Object,
      default: () => {},
    },
    getSumVoteCount: {
      type: Function,
      default: () => {},
    },
    selectTab: {
      type: Function,
      default: () => {},
    },
    toggleShowSubmission: {
      type: Function,
      default: () => {},
    },
    toggleShowVotes: {
      type: Function,
      default: () => {},
    },
    pickOption: {
      type: Function,
      default: () => {},
    },
    removePickOption: {
      type: Function,
      default: () => {},
    },
    itemPicked: {
      type: Object,
      default: () => {},
    },
    showIdea: {
      type: Boolean,
      default: false,
    },
    checkShowAudienceInfo: {
      type: Function,
      default: () => {},
    },
    checkShowAudienceFields: {
      type: Function,
      default: () => {},
    },
    setToColorPalette: {
      type: Function,
      default: () => {},
    },
    scrollShow: {
      type: Boolean,
      default: false,
    },
    scrollDisable: {
      type: Boolean,
      default: false,
    },
    scrollTop: {
      type: Function,
      default: () => {},
    },
    scrollBottom: {
      type: Function,
      default: () => {},
    },
    isVotingStepResult: {
      type: Boolean,
      default: false,
    },
    handleRemoveResponse: {
      type: Function,
      default: () => {},
    },
    isVotingStepSubmission: {
      type: Boolean,
      default: false,
    },
    isVoteCountTextVisible: {
      type: Boolean,
      default: false,
    },
    arrowScrollTopVisible: {
      type: Boolean,
      default: false,
    },
    arrowScrollBottomVisible: {
      type: Boolean,
      default: false,
    },
    arrowAnimation: {
      type: String,
      default: '',
    },
    setBoxShadowValue: {
      type: Function,
      default: () => {},
    },
    getRankOrderBg: {
      type: Function,
      default: () => {},
    },
    isBlurAnswerContent: {
      type: Boolean,
      default: false,
    },
    loadedIdeas: {
      type: Boolean,
      default: false,
    },
    isTextColourLight: {
      type: Boolean,
      default: false,
    },
    slideActiveBaseColour: {
      type: String,
      default: '',
    },
  },
}
</script>

<style lang="scss" scoped>
@import "@/assets/scss-v2/variables.scss";

.fix-height {
  min-height: 50px;
}
.voting-ideas-content {
  @apply flex flex-row flex-auto px-12;
  & ::v-deep .masonry-openended-container {
    padding: 0 10px;
  }

  & ::v-deep .pre-line {
    cursor: default;
    -webkit-user-select: text;
    -khtml-user-select: text;
    -moz-user-select: text;
    -o-user-select: text;
    user-select: text;
  }
}
.left-column {
  width: 220px;

  ul {
    padding-left: 5px;
    li {
      list-style: none;
      padding: 15px 0;
      cursor: pointer;
      font-size: 20px;
      transition: background-color 0.2s;
      opacity: 0.8;
      border-left: 3px solid transparent;
      &.disabled {
        cursor: not-allowed;
        opacity: 0.5;
        .voting-ideas-step {
          .voting-ideas-step-number {
            background-color: rgba(240,241,241,1);
            color: inherit;
            &.dark {
              background-color: rgba(240,241,241,0.1);
            }
          }
          .voting-ideas-step-text {
            color: inherit;
          }
        }
      }
      .voting-ideas-step {
        @apply capitalize font-bold;
        .voting-ideas-step-number {
          @apply bg-base-60 border border-base-0;
          @apply text-base-0 text-xl font-normal;
          @apply rounded-full w-8 flex items-center justify-center;
          height: 32px;
          width: 32px;
          line-height: 35px;
        }
        .voting-ideas-step-text {
          @apply pl-2;
        }
        &.active {
          .voting-ideas-step-number {
            @apply bg-primary-90;
          }
        }
      }
    }
  }
}
.right-column {
  @apply flex-1 relative;
  .pick-openended {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 128;
  }
  .open-ended-picked {
    transition: all 0.5s;
    cursor: pointer;
    max-height: 100%;
    overflow-y: auto;
    -ms-overflow-style: auto;
    overflow-x: hidden;
    word-break: break-word;
    min-width: 200px;
    border-radius: 10px;
    font-size: 25px;
    max-width: 81%;
    position: relative;
    padding: 25px 30px;
    .content-rank-order {
      display: none;
    }
    &.has-ideas-rank {
      overflow: visible;
      position: relative;

      .content-rank-order {
        display: inline-block;
        @apply absolute -top-5 -left-5 w-10 h-10;
        @apply rounded-full z-10 flex items-center justify-center;
        color: inherit;
      }
    }
    .remove-icon {
      top: -5px;
      height: 40px;
      .tooltip-btn {
        height: 40px;
        width: 35px;
      }
    }
    &:hover {
      .remove-icon {
        opacity: 0.5;
        &:hover {
          opacity: 1;
        }
      }
    }
  }
  .has-audience-info {
    .info--other-team, .info--other-email {
      font-size: 0.8em;
    }
    .audience-info {
      min-height: 48px;
    }
    &.open-ended-picked {
      padding: 0 !important;
      &.has-ideas-rank .picked-title {
        padding-right: 50px;
      }
      .picked-title {
        padding: 20px 15px 30px 20px;
        max-height: 202px;
        min-height: 81px;
        overflow-y: auto;
        -ms-overflow-style: auto;
        font-size: 26px;
        line-height: 35px;
        .has-transition {
          transition: all 0.2s ease;
        }
        .hide-submission {
          filter: blur(4px);
          opacity: 0.2;
        }
      }
      .audience-info {
        padding: 20px;
        line-height: 1.2;
        &.has-vote-count-content {
          display: flex;
          align-items: center;
        }
        .info--vote-count {
          @apply flex text-right flex-1 justify-end pl-16;
          .vote-count-container {
            @apply px-2 w-max border border-base-50;
            margin-right: 0 !important; // overwrite bootstrap
            height: 2.25rem;
          }
        }
        .info--emoji {
          min-width: 35px;
          font-size: 30px;
          display: inline-block;
        }
      }
    }
  }
  .audience-info {
    color: inherit;
    border-radius: 0 0 8px 8px;
    background-color: rgba(240,241,241,1);
    &.dark {
      background-color: rgba(240,241,241,0.1);
    }
  }
}
.animate__animated {
  animation-duration: 0.2s;
}
.arrow-grid.no-absolute {
  position: initial;
  transform: initial;
}
.arrow-grid {
  text-align: center;
  font-size: 20px;
  text-align: center;
  font-size: 20px;
  cursor: pointer;
  position: absolute;
  left: 50%;
  transform: translateX(-25%);
  i {
    &.disabled {
      color: #888;
    }

    &:hover {
      color: #666;
    }
  }
}
</style>
